import { Controller } from "@hotwired/stimulus"
import { get, set as storeSet, subscribe } from "../lib/session_store"

const PIP_STORAGE_KEY = "apexracing:video:pip"
const MIN_WIDTH_RATIO = 1 / 6
const MAX_WIDTH_RATIO = 1 / 4

export default class extends Controller {
  static targets = [
    "urlInput",
    "playerContainer",
    "pipContainer",
    "pipDrag",
    "pipResize",
    "pipClose",
    "pipToggle",
    "calibrationPanel",
    "lapSelect",
    "syncStatus",
    "syncPointBtn",
    "syncBadge",
    "resetSyncBtn",
  ]
  static values = {
    sessionId: String,
    videoUrl: String,
    videoId: String,
    syncOffset: { type: Number, default: NaN },
    syncAnchorLap: { type: Number, default: 0 },
  }

  connect() {
    this.player = null
    this.playerReady = false
    this.pipActive = false
    this._originalParent = null
    this._dragState = null
    this._resizeState = null

    this._offset = get("syncOffset") ?? NaN

    // Cache direct references before PiP may be moved out of the controller's scope
    this._pipEl = this.pipContainerTarget
    this._pipDragEl = this.hasPipDragTarget ? this.pipDragTarget : null
    this._pipResizeEl = this.hasPipResizeTarget ? this.pipResizeTarget : null
    this._pipCloseEl = this.hasPipCloseTarget ? this.pipCloseTarget : null
    this._playerEl = this.playerContainerTarget

    // Restore PiP preferences
    this._pipPrefs = this._loadPipPrefs()

    // Subscribe to store changes
    this._unsubs = [
      subscribe("playbackMode", (mode) => {
        // If something else took over while we're polling, stop
        if (mode !== "video" && this._pollRaf) {
          this._stopPoll()
          this.pauseVideo()
        }
        this._updateSyncBadge()
      }),
      subscribe("activeLapNumber", (lapNumber) => {
        // Always seek video on lap click (no mode guard — feedback loop
        // prevented by pit-wall only writing activeLapNumber during live)
        this._seekToLap(lapNumber)
      }),
      subscribe("syncOffset", (offset) => {
        this._offset = offset ?? NaN
        this._updateSyncBadge()
      }),
    ]

    // Push offset to store so sync works even before the player is visible
    if (this._isCalibrated()) {
      storeSet("syncOffset", this.syncOffsetValue)
    }

    // Pre-fetch lap session times so seeking works before the player is visible
    if (this.sessionIdValue) {
      this._loadLapTimes()
    }

    // If we already have a video URL, load it eagerly (offscreen if hidden)
    if (this.videoUrlValue) {
      this._loadEager()
    }
  }

  disconnect() {
    this._stopPoll()
    this._unsubs?.forEach((fn) => fn())
    if (this._visibilityRaf) {
      cancelAnimationFrame(this._visibilityRaf)
      this._visibilityRaf = null
    }
    if (this._offscreenEl) {
      this._offscreenEl.remove()
      this._offscreenEl = null
    }
    if (this.player) {
      this.player.destroy()
      this.player = null
    }
    this._restorePipContainer()
  }

  // --- Eager loading with offscreen fallback for hidden containers ---

  _loadEager() {
    this._ensureYTApi().then(() => {
      if (this.element.offsetParent !== null) {
        this._createPlayer(this.videoIdValue)
      } else {
        this._offscreenEl = document.createElement("div")
        this._offscreenEl.style.cssText =
          "position:fixed;left:-9999px;top:0;width:320px;height:180px;overflow:hidden"
        document.body.appendChild(this._offscreenEl)
        this._createPlayerIn(this._offscreenEl, this.videoIdValue)
        this._pollForVisibility()
      }
    })
  }

  _pollForVisibility() {
    const check = () => {
      if (!this.element.isConnected) return
      if (this.element.offsetParent !== null) {
        this._movePlayerToInline()
      } else {
        this._visibilityRaf = requestAnimationFrame(check)
      }
    }
    this._visibilityRaf = requestAnimationFrame(check)
  }

  _movePlayerToInline() {
    const currentTime = this.getCurrentTime()
    this._destroyOffscreen()
    this._createPlayer(this.videoIdValue)
    if (currentTime > 0) {
      this._pendingSeek = currentTime
    }
  }

  _destroyOffscreen() {
    if (this._visibilityRaf) {
      cancelAnimationFrame(this._visibilityRaf)
      this._visibilityRaf = null
    }
    if (this.player) {
      this.player.destroy()
      this.player = null
      this.playerReady = false
    }
    if (this._offscreenEl) {
      this._offscreenEl.remove()
      this._offscreenEl = null
    }
  }

  // --- URL submission ---

  loadUrl() {
    const url = this.urlInputTarget.value.trim()
    if (!url) return

    const videoId = this._extractVideoId(url)
    if (!videoId) {
      this.urlInputTarget.classList.add("ring-red-500", "ring-2")
      return
    }

    this.urlInputTarget.classList.remove("ring-red-500", "ring-2")

    this._patchVideo({ video_url: url })
    this.videoUrlValue = url
    this.videoIdValue = videoId

    this._ensureYTApi().then(() => this._createPlayer(videoId))
  }

  // --- YouTube IFrame API ---

  _ensureYTApi() {
    if (window.YT && window.YT.Player) return Promise.resolve()

    return new Promise((resolve) => {
      if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const existing = window.onYouTubeIframeAPIReady
        window.onYouTubeIframeAPIReady = () => {
          if (existing) existing()
          resolve()
        }
        return
      }

      window.onYouTubeIframeAPIReady = resolve
      const tag = document.createElement("script")
      tag.src = "https://www.youtube.com/iframe_api"
      document.head.appendChild(tag)
    })
  }

  _createPlayer(videoId) {
    this._createPlayerIn(this._playerEl, videoId)
    this._playerEl.classList.remove("hidden")
    if (this.hasPipToggleTarget) this.pipToggleTarget.classList.remove("hidden")
    if (this.hasCalibrationPanelTarget)
      this.calibrationPanelTarget.classList.remove("hidden")
  }

  _createPlayerIn(container, videoId) {
    if (this.player) {
      this.player.destroy()
      this.player = null
      this.playerReady = false
    }

    const el = document.createElement("div")
    el.id = `yt-player-${this.sessionIdValue}`
    container.innerHTML = ""
    container.appendChild(el)

    this.player = new window.YT.Player(el.id, {
      videoId,
      width: "100%",
      height: "100%",
      playerVars: {
        autoplay: 0,
        modestbranding: 1,
        rel: 0,
      },
      events: {
        onReady: () => {
          this.playerReady = true
          if (this._pendingSeek != null) {
            this.seekTo(this._pendingSeek)
            this.pauseVideo()
            this._pendingSeek = null
          } else if (this._isCalibrated() && this._lapStarts) {
            this.goToSyncPoint({ autoSeek: true })
          }
          this.dispatch("ready")
        },
        onStateChange: (e) => this._onStateChange(e),
      },
    })
  }

  _onStateChange(event) {
    const state = event.data
    if (state === window.YT.PlayerState.PLAYING) {
      this._onVideoPlay()
    } else if (
      state === window.YT.PlayerState.PAUSED ||
      state === window.YT.PlayerState.ENDED
    ) {
      this._onVideoPause()
    }
  }

  // Public API
  getCurrentTime() {
    if (!this.playerReady || !this.player) return 0
    return this.player.getCurrentTime()
  }

  seekTo(time) {
    if (!this.playerReady || !this.player) return
    this.player.seekTo(time, true)
  }

  pauseVideo() {
    if (!this.playerReady || !this.player) return
    this.player.pauseVideo()
  }

  playVideo() {
    if (!this.playerReady || !this.player) return
    this.player.playVideo()
  }

  // --- Sync logic ---

  _onVideoPlay() {
    if (!this._hasOffset()) return

    storeSet("playbackMode", "video")
    this._updateSyncBadge()
    this._startPoll()
  }

  _onVideoPause() {
    this._stopPoll()
    if (get("playbackMode") === "video") {
      storeSet("playbackMode", "idle")
    }
  }

  resetSync() {
    this._updateSyncBadge()
    if (this._hasOffset()) {
      this.playVideo()
    }
  }

  onDrawerClose() {
    if (this._pollRaf) {
      this._stopPoll()
      this.pauseVideo()
      storeSet("playbackMode", "idle")
    }
  }

  _startPoll() {
    this._stopPoll()
    this._pollRaf = true
    const tick = () => {
      if (!this._pollRaf) return
      this._pollTick()
      this._pollAnimId = requestAnimationFrame(tick)
    }
    this._pollAnimId = requestAnimationFrame(tick)
  }

  _stopPoll() {
    this._pollRaf = false
    if (this._pollAnimId) {
      cancelAnimationFrame(this._pollAnimId)
      this._pollAnimId = null
    }
  }

  _pollTick() {
    if (get("playbackMode") !== "video") return
    if (!this._hasOffset()) return

    const videoTime = this.getCurrentTime()
    const sessionTime = videoTime - this._offset

    storeSet("sessionTime", sessionTime)
  }

  _updateSyncBadge() {
    const badge = this.hasSyncBadgeTarget
      ? this.syncBadgeTarget
      : this.element.querySelector("[data-media-sync-badge]")
    if (!badge) return

    const resetBtn = this.hasResetSyncBtnTarget ? this.resetSyncBtnTarget : null
    const mode = get("playbackMode")

    if (mode === "video") {
      badge.textContent = "Synced"
      badge.classList.remove("bg-yellow-500/20", "text-yellow-400", "hidden")
      badge.classList.add("bg-green-500/20", "text-green-400")
      if (resetBtn) resetBtn.classList.add("hidden")
    } else if (this._hasOffset()) {
      badge.textContent = "Calibrated"
      badge.classList.remove("bg-yellow-500/20", "text-yellow-400", "hidden")
      badge.classList.add("bg-green-500/20", "text-green-400")
      if (resetBtn) resetBtn.classList.add("hidden")
    } else {
      badge.classList.add("hidden")
      if (resetBtn) resetBtn.classList.add("hidden")
    }
  }

  _hasOffset() {
    return !isNaN(this._offset)
  }

  // --- PiP mode ---

  enterPipFromExternal(event) {
    this._enterPipFromExternal(event.detail?.anchor)
  }

  togglePip() {
    if (this.pipActive) {
      this._exitPip()
    } else {
      this._enterPip()
    }
  }

  _enterPipFromExternal(anchor) {
    if (this.pipActive || !this.videoUrlValue) return

    if (this.playerReady) {
      this._enterPip(anchor)
    } else {
      const wait = setInterval(() => {
        if (this.playerReady) {
          clearInterval(wait)
          this._enterPip(anchor)
        }
      }, 100)
      setTimeout(() => clearInterval(wait), 5000)
    }
  }

  _enterPip(anchor) {
    const pip = this._pipEl
    const currentTime = this.getCurrentTime()

    // 1. Destroy existing players
    this._destroyOffscreen()
    if (this.player) {
      this.player.destroy()
      this.player = null
      this.playerReady = false
    }

    // 2. Hide inline player, show PiP placeholder
    this._playerEl.classList.add("hidden")
    this._showPipPlaceholder()

    // 3. Always append to body with position:fixed.
    //    If anchor is provided, compute initial position from anchor rect.
    this._originalParent = pip.parentElement
    document.body.appendChild(pip)
    pip.style.position = "fixed"

    pip.classList.remove("hidden")
    this.pipActive = true

    if (anchor) {
      this._applyPositionNearAnchor(anchor)
    } else {
      this._applyPipPosition()
    }

    // 4. NOW create the player (IFrame gets real dimensions)
    const pipSlot = pip.querySelector("[data-media-target='pipPlayer']")
    if (pipSlot) {
      if (currentTime > 0) this._pendingSeek = currentTime
      this._createPlayerIn(pipSlot, this.videoIdValue)
    }

    // 5. Bind interactions
    this._bindPipInteractions()
  }

  _exitPip() {
    const pip = this._pipEl
    const currentTime = this.getCurrentTime()

    if (this.player) {
      this.player.destroy()
      this.player = null
      this.playerReady = false
    }

    const pipSlot = pip.querySelector("[data-media-target='pipPlayer']")
    if (pipSlot) pipSlot.innerHTML = ""

    pip.classList.add("hidden")

    // Reset positioning styles
    pip.style.position = ""
    pip.style.bottom = ""
    pip.style.right = ""
    pip.style.top = ""
    pip.style.left = ""

    this._restorePipContainer()

    this.pipActive = false
    this._unbindPipInteractions()

    // Remove placeholder, restore inline player
    this._removePipPlaceholder()

    if (currentTime > 0) this._pendingSeek = currentTime
    this._createPlayer(this.videoIdValue)
  }

  _showPipPlaceholder() {
    const parent = this._playerEl.parentElement
    if (!parent) return

    const placeholder = document.createElement("div")
    placeholder.id = "pip-placeholder"
    placeholder.className =
      "flex flex-col items-center justify-center gap-2 py-8 text-white/60 text-sm"
    placeholder.innerHTML = `
      <span>Video is in Picture-in-Picture</span>
      <button class="px-3 py-1 text-xs rounded bg-white/10 hover:bg-white/20 text-white transition-colors"
              data-action="media#closePip">Exit PiP</button>
    `
    parent.insertBefore(placeholder, this._playerEl.nextSibling)
  }

  _removePipPlaceholder() {
    const placeholder = document.getElementById("pip-placeholder")
    if (placeholder) placeholder.remove()
    this._playerEl.classList.remove("hidden")
  }

  _restorePipContainer() {
    if (this._originalParent && this._pipEl) {
      this._originalParent.appendChild(this._pipEl)
      this._originalParent = null
    }
  }

  _applyPipPosition() {
    const pip = this._pipEl
    const { corner, width } = this._pipPrefs
    const margin = 16

    pip.style.width = `${width}px`
    pip.style.aspectRatio = "16 / 9"

    const rect = pip.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    let top, left
    switch (corner) {
      case "top-left":
        top = margin
        left = margin
        break
      case "top-right":
        top = margin
        left = vw - rect.width - margin
        break
      case "bottom-left":
        top = vh - rect.height - margin
        left = margin
        break
      case "bottom-right":
      default:
        top = vh - rect.height - margin
        left = vw - rect.width - margin
        break
    }

    pip.style.top = `${top}px`
    pip.style.left = `${left}px`
  }

  _applyPositionNearAnchor(anchor) {
    const pip = this._pipEl
    const anchorRect = anchor.getBoundingClientRect()
    const margin = 8

    // Size to ~50% of anchor width
    const width = Math.round(anchorRect.width * 0.5)
    pip.style.width = `${width}px`
    pip.style.aspectRatio = "16 / 9"

    // Position at bottom-left of anchor, using fixed viewport coordinates
    const top = anchorRect.bottom - (width * 9) / 16 - margin
    const left = anchorRect.left + margin

    pip.style.top = `${top}px`
    pip.style.left = `${left}px`
    pip.style.bottom = ""
    pip.style.right = ""

    this._pipPrefs.width = width
    this._pipPrefs.corner = "bottom-left"
    this._savePipPrefs()
  }

  _snapToCorner() {
    const pip = this._pipEl
    const rect = pip.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    const vw = window.innerWidth
    const vh = window.innerHeight

    let corner
    if (cx < vw / 2 && cy < vh / 2) corner = "top-left"
    else if (cx >= vw / 2 && cy < vh / 2) corner = "top-right"
    else if (cx < vw / 2 && cy >= vh / 2) corner = "bottom-left"
    else corner = "bottom-right"

    this._pipPrefs.corner = corner
    this._savePipPrefs()
    this._applyPipPosition()
  }

  _bindPipInteractions() {
    this._onDragDown = (e) => this._startDrag(e)
    this._onDragMove = (e) => this._moveDrag(e)
    this._onDragUp = () => this._endDrag()
    this._onResizeDown = (e) => this._startResize(e)
    this._onResizeMove = (e) => this._moveResize(e)
    this._onResizeUp = () => this._endResize()
    this._onCloseClick = () => this._exitPip()

    if (this._pipDragEl) {
      this._pipDragEl.addEventListener("pointerdown", this._onDragDown)
    }
    if (this._pipResizeEl) {
      this._pipResizeEl.addEventListener("pointerdown", this._onResizeDown)
    }
    if (this._pipCloseEl) {
      this._pipCloseEl.addEventListener("click", this._onCloseClick)
    }
    document.addEventListener("pointermove", this._onDragMove)
    document.addEventListener("pointermove", this._onResizeMove)
    document.addEventListener("pointerup", this._onDragUp)
    document.addEventListener("pointerup", this._onResizeUp)
  }

  _unbindPipInteractions() {
    if (this._pipDragEl) {
      this._pipDragEl.removeEventListener("pointerdown", this._onDragDown)
    }
    if (this._pipResizeEl) {
      this._pipResizeEl.removeEventListener("pointerdown", this._onResizeDown)
    }
    if (this._pipCloseEl) {
      this._pipCloseEl.removeEventListener("click", this._onCloseClick)
    }
    document.removeEventListener("pointermove", this._onDragMove)
    document.removeEventListener("pointermove", this._onResizeMove)
    document.removeEventListener("pointerup", this._onDragUp)
    document.removeEventListener("pointerup", this._onResizeUp)
  }

  _startDrag(e) {
    e.preventDefault()
    const pip = this._pipEl
    this._dragState = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: pip.offsetLeft,
      origTop: pip.offsetTop,
    }
  }

  _moveDrag(e) {
    if (!this._dragState) return
    const pip = this._pipEl
    pip.style.left = `${this._dragState.origLeft + e.clientX - this._dragState.startX}px`
    pip.style.top = `${this._dragState.origTop + e.clientY - this._dragState.startY}px`
  }

  _endDrag() {
    if (!this._dragState) return
    this._dragState = null
    this._snapToCorner()
  }

  _startResize(e) {
    e.preventDefault()
    e.stopPropagation()
    this._resizeState = {
      startX: e.clientX,
      origWidth: this._pipEl.offsetWidth,
    }
  }

  _moveResize(e) {
    if (!this._resizeState) return
    const vw = window.innerWidth
    const minW = vw * MIN_WIDTH_RATIO
    const maxW = vw * MAX_WIDTH_RATIO
    const newW = Math.max(
      minW,
      Math.min(
        maxW,
        this._resizeState.origWidth + e.clientX - this._resizeState.startX,
      ),
    )
    this._pipEl.style.width = `${newW}px`
  }

  _endResize() {
    if (!this._resizeState) return
    this._pipPrefs.width = this._pipEl.offsetWidth
    this._savePipPrefs()
    this._resizeState = null
    this._snapToCorner()
  }

  closePip() {
    this._exitPip()
  }

  // --- Calibration ---

  async _loadLapTimes() {
    try {
      const resp = await fetch(
        `/sessions/${this.sessionIdValue}/lap_session_times.json`,
      )
      if (!resp.ok) return

      const data = await resp.json()
      this._lapStarts = data.lap_starts || {}

      if (this.hasLapSelectTarget) {
        this.lapSelectTarget.innerHTML = ""
        const defaultOpt = document.createElement("option")
        defaultOpt.value = ""
        defaultOpt.textContent = "Select lap..."
        this.lapSelectTarget.appendChild(defaultOpt)

        for (const [lap] of Object.entries(this._lapStarts).sort(
          (a, b) => Number(a[0]) - Number(b[0]),
        )) {
          const opt = document.createElement("option")
          opt.value = lap
          opt.textContent = `Lap ${lap}`
          this.lapSelectTarget.appendChild(opt)
        }
      }

      this._updateSyncStatus()

      if (this._isCalibrated()) {
        this.goToSyncPoint({ autoSeek: true })
      }
    } catch (_) {
      // silently fail
    }
  }

  goToSyncPoint({ autoSeek = false } = {}) {
    if (!this._isCalibrated() || !this._lapStarts) return

    const lapNum = String(this.syncAnchorLapValue)
    const sessionTime = this._lapStarts[lapNum]
    if (sessionTime == null) return

    const videoTime = sessionTime + this.syncOffsetValue
    if (this.playerReady && this.player) {
      this.seekTo(videoTime)
      if (autoSeek) this.pauseVideo()
    }

    storeSet("sessionTime", sessionTime)

    // Only write activeLapNumber for explicit user clicks, not auto-seek on init
    if (!autoSeek) {
      if (this._lastSyncLap !== lapNum) {
        this._lastSyncLap = lapNum
        storeSet("activeLapNumber", this.syncAnchorLapValue)
      }
    }

    if (this.hasLapSelectTarget) {
      this.lapSelectTarget.value = lapNum
    }

    storeSet("syncOffset", this.syncOffsetValue)
  }

  setSyncPoint() {
    if (!this.playerReady || !this.hasLapSelectTarget) return

    const lapNum = this.lapSelectTarget.value
    if (!lapNum || !this._lapStarts[lapNum]) return

    const videoTime = this.getCurrentTime()
    const sessionTime = this._lapStarts[lapNum]
    const offset = videoTime - sessionTime

    this._patchVideo({
      video_sync_offset: offset,
      video_sync_anchor_lap: parseInt(lapNum, 10),
    })

    this.syncOffsetValue = offset
    this.syncAnchorLapValue = parseInt(lapNum, 10)
    this._updateSyncStatus()

    storeSet("syncOffset", offset)
  }

  _updateSyncStatus() {
    if (!this.hasSyncStatusTarget) return

    if (!isNaN(this.syncOffsetValue) && this.syncAnchorLapValue > 0) {
      this.syncStatusTarget.textContent = `Synced at Lap ${this.syncAnchorLapValue} (offset: ${this.syncOffsetValue.toFixed(1)}s)`
      this.syncStatusTarget.classList.remove("text-app-text-muted")
      this.syncStatusTarget.classList.add("text-green-600")
    } else {
      this.syncStatusTarget.textContent = "Not calibrated"
      this.syncStatusTarget.classList.remove("text-green-600")
      this.syncStatusTarget.classList.add("text-app-text-muted")
    }
  }

  // --- Server communication ---

  async _patchVideo(fields) {
    try {
      const csrfToken = document.querySelector(
        "meta[name='csrf-token']",
      )?.content
      await fetch(`/sessions/${this.sessionIdValue}/video.json`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ session: fields }),
      })
    } catch (_) {
      // silently fail
    }
  }

  // --- Helpers ---

  _seekToLap(lapNumber) {
    if (
      !this._isCalibrated() ||
      !this._lapStarts ||
      !this.playerReady ||
      !this.player
    )
      return

    const sessionTime = this._lapStarts[String(lapNumber)]
    if (sessionTime == null) return

    const videoTime = sessionTime + this.syncOffsetValue
    this.seekTo(videoTime)
  }

  _isCalibrated() {
    return !isNaN(this.syncOffsetValue) && this.syncAnchorLapValue > 0
  }

  _extractVideoId(url) {
    try {
      const u = new URL(url)
      if (u.hostname === "youtu.be") return u.pathname.slice(1).split("/")[0]
      if (u.hostname.includes("youtube.com")) return u.searchParams.get("v")
    } catch (_) {
      // invalid URL
    }
    return null
  }

  _loadPipPrefs() {
    try {
      const stored = localStorage.getItem(PIP_STORAGE_KEY)
      if (stored) return JSON.parse(stored)
    } catch (_) {
      // ignore
    }
    return { corner: "bottom-right", width: Math.round(window.innerWidth / 5) }
  }

  _savePipPrefs() {
    try {
      localStorage.setItem(PIP_STORAGE_KEY, JSON.stringify(this._pipPrefs))
    } catch (_) {
      // ignore
    }
  }
}
